import React, { useState, useEffect, useCallback } from "react";
import { obtenerListaArticulos } from "../../../store/Articulos/thunkArticulos";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { CrearArticulo } from "./CrearArticulo";
import { EditarArticulo } from "./EditarArticulo";
import { hotelesOpciones } from "../../../hook/Arreglos";
import "../../css/ColorTable.css";

const hotelLabelByValue = hotelesOpciones.reduce((acc, hotel) => {
    acc[hotel.value] = hotel.label;
    return acc;
}, {});

const hotelValueByLabel = hotelesOpciones.reduce((acc, hotel) => {
    acc[hotel.label] = hotel.value;
    return acc;
}, {});

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: "48px",
        borderRadius: "14px",
        borderColor: state.isFocused ? "#0f766e" : "#d7dee7",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(15, 118, 110, 0.12)" : "none",
        backgroundColor: "#ffffff",
        paddingLeft: "4px",
        "&:hover": {
            borderColor: "#0f766e",
        },
    }),
    placeholder: (base) => ({
        ...base,
        color: "#6b7280",
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: "#e6fffb",
        borderRadius: "999px",
        paddingInline: "4px",
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: "#115e59",
        fontWeight: 600,
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: "#115e59",
        borderRadius: "999px",
        ":hover": {
            backgroundColor: "#99f6e4",
            color: "#134e4a",
        },
    }),
    menu: (base) => ({
        ...base,
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.14)",
    }),
};

const normalizeHotelIds = (hotelesSeleccionados) => {
    if (Array.isArray(hotelesSeleccionados)) {
        return hotelesSeleccionados.map((hotel) => `${hotel}`);
    }

    if (typeof hotelesSeleccionados === "string") {
        try {
            const parsedHotels = JSON.parse(hotelesSeleccionados);

            if (Array.isArray(parsedHotels)) {
                return parsedHotels.map((hotel) => `${hotel}`);
            }
        } catch (error) {
            return hotelesSeleccionados
                .split(",")
                .map((hotel) => hotel.trim())
                .filter(Boolean);
        }
    }

    return [];
};

const getHotelLabels = (articulo) => {
    const hotelIds = normalizeHotelIds(articulo.hotelesSeleccionados);

    if (hotelIds.length > 0) {
        return hotelIds.map((hotelId) => hotelLabelByValue[hotelId] || hotelId);
    }

    return articulo.nombre_hotel ? [articulo.nombre_hotel] : [];
};

const getSortedHotelIds = (articulo, selectedHotelValues = []) => {
    const hotelIds = normalizeHotelIds(articulo.hotelesSeleccionados);

    if (hotelIds.length === 0) {
        return [];
    }

    return [...hotelIds].sort((a, b) => {
        const aSelected = selectedHotelValues.includes(a);
        const bSelected = selectedHotelValues.includes(b);

        if (aSelected === bSelected) {
            return 0;
        }

        return aSelected ? -1 : 1;
    });
};

const getTaxLabel = (value, label) => (Number(value) === 1 ? label : "No aplica");

const normalizeSpecificPrices = (preciosEspecificos) => {
    if (!preciosEspecificos) {
        return {};
    }

    if (typeof preciosEspecificos === "string") {
        try {
            const parsedPrices = JSON.parse(preciosEspecificos);
            return parsedPrices && typeof parsedPrices === "object" ? parsedPrices : {};
        } catch (error) {
            return {};
        }
    }

    return typeof preciosEspecificos === "object" ? preciosEspecificos : {};
};

const buildHotelOption = (hotelValue, hotelLabel) => ({
    value: `${hotelValue}`,
    label: hotelLabelByValue[`${hotelValue}`] || hotelLabel || `${hotelValue}`,
});

const getHotelPrices = (articulo, selectedHotelValues = []) => {
    const hotelIds = getSortedHotelIds(articulo, selectedHotelValues);
    const specificPrices = normalizeSpecificPrices(articulo.precios_especificos);

    return hotelIds
        .map((hotelId) => {
            const hotelLabel = hotelLabelByValue[hotelId] || hotelId;
            const hotelPrice = specificPrices[hotelId];

            if (hotelPrice === undefined || hotelPrice === null || hotelPrice === "") {
                return null;
            }

            return {
                hotelId,
                hotelLabel,
                hotelPrice,
            };
        })
        .filter(Boolean);
};

const formatDescriptionLines = (description = "") => {
    const normalizedDescription = `${description}`.replace(/\r\n/g, "\n").trim();

    if (!normalizedDescription) {
        return { intro: "", bullets: [] };
    }

    const [introPart, ...bulletParts] = normalizedDescription.split("•");

    return {
        intro: introPart.trim(),
        bullets: bulletParts.map((bullet) => bullet.trim()).filter(Boolean),
    };
};

export const ListaArticulos = () => {
    const dispatch = useDispatch();
    const [articulos, setArticulos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pageSize, setPageSize] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [nombreFiltro, setNombreFiltro] = useState("");
    const [hotelFiltros, setHotelFiltros] = useState([]);
    const [hoteles, setHoteles] = useState([]);
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [selectedArticulo, setSelectedArticulo] = useState(null);

    const handleOpenCreateModal = () => setOpenCreateModal(true);
    const handleCloseCreateModal = () => setOpenCreateModal(false);

    const handleOpenEditModal = (articulo) => {
        setSelectedArticulo(articulo);
        setOpenEditModal(true);
    };

    const handleCloseEditModal = () => {
        setSelectedArticulo(null);
        setOpenEditModal(false);
    };

    const fetchArticulos = useCallback(
        async (estado) => {
            try {
                setLoading(true);
                setError(null);

                const response = await dispatch(obtenerListaArticulos(estado));
                const articulosResponse = response || [];
                const hotelesUnicos = new Map();

                setArticulos(articulosResponse);

                articulosResponse.forEach((articulo) => {
                    const hotelIds = normalizeHotelIds(articulo.hotelesSeleccionados);

                    if (hotelIds.length > 0) {
                        hotelIds.forEach((hotelId) => {
                            if (!hotelesUnicos.has(hotelId)) {
                                hotelesUnicos.set(hotelId, buildHotelOption(hotelId));
                            }
                        });
                        return;
                    }

                    const fallbackHotelValue = hotelValueByLabel[articulo.nombre_hotel];

                    if (articulo.nombre_hotel && fallbackHotelValue && !hotelesUnicos.has(fallbackHotelValue)) {
                        hotelesUnicos.set(fallbackHotelValue, buildHotelOption(fallbackHotelValue, articulo.nombre_hotel));
                    }

                    if (articulo.nombre_hotel && !fallbackHotelValue && !hotelesUnicos.has(articulo.nombre_hotel)) {
                        hotelesUnicos.set(articulo.nombre_hotel, buildHotelOption(articulo.nombre_hotel, articulo.nombre_hotel));
                    }
                });

                setHoteles(Array.from(hotelesUnicos.values()));
            } catch (err) {
                console.error("Error al obtener los articulos:", err);
                setError("No se pudo cargar la lista de articulos.");
            } finally {
                setLoading(false);
            }
        },
        [dispatch],
    );

    useEffect(() => {
        fetchArticulos(1);
    }, [fetchArticulos]);

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const handleNombreFiltroChange = (e) => {
        setNombreFiltro(e.target.value);
        setCurrentPage(1);
    };

    const handleHotelFiltrosChange = (selectedOptions) => {
        setHotelFiltros(selectedOptions || []);
        setCurrentPage(1);
    };

    const selectedHotelValues = hotelFiltros.map((hotel) => `${hotel.value}`);

    const filteredArticulos = articulos
        .filter((articulo) => {
            const nombreMatch = articulo.nombre_articulo.toLowerCase().includes(nombreFiltro.toLowerCase());
            const hotelIds = normalizeHotelIds(articulo.hotelesSeleccionados);
            const hotelLabels = getHotelLabels(articulo);
            const hotelMatch =
                hotelFiltros.length === 0 ||
                hotelFiltros.some((hotel) => hotelIds.includes(hotel.value) || hotelLabels.includes(hotel.value));

            return nombreMatch && hotelMatch;
        })
        .sort((a, b) => a.nombre_articulo.localeCompare(b.nombre_articulo, "es", { sensitivity: "base" }));

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedArticulos = filteredArticulos.slice(startIndex, endIndex);
    const totalPages = Math.max(1, Math.ceil(filteredArticulos.length / pageSize));

    if (loading) return <div className="articulos-feedback">Cargando articulos...</div>;
    if (error) return <div className="articulos-feedback articulos-feedback--error">{error}</div>;

    return (
        <div className="articulos-view">
            <section className="articulos-shell">
                <div className="articulos-hero">
                    <div>
                        <span className="articulos-eyebrow">Catalogo de articulos</span>
                        <h1 className="articulos-title">Catalogo de articulos por hotel</h1>
                        <p className="articulos-subtitle">
                            Consulta articulos, revisa precios por hotel y administra su configuracion desde un solo lugar.
                        </p>
                    </div>
                    <button className="articulos-primary-btn" onClick={handleOpenCreateModal}>
                        Crear articulo
                    </button>
                </div>

                <div className="articulos-toolbar">
                    <div className="articulos-filter-grid">
                        <div className="articulos-field">
                            <label className="articulos-label" htmlFor="articuloNombreFiltro">
                                Buscar articulo
                            </label>
                            <input
                                id="articuloNombreFiltro"
                                type="text"
                                className="articulos-input"
                                placeholder="Escribe nombre de articulo"
                                value={nombreFiltro}
                                onChange={handleNombreFiltroChange}
                            />
                        </div>

                        <div className="articulos-field">
                            <label className="articulos-label">Filtrar por hotel</label>
                            <Select
                                isMulti
                                options={hoteles}
                                value={hotelFiltros}
                                onChange={handleHotelFiltrosChange}
                                placeholder="Seleccionar hoteles"
                                styles={selectStyles}
                            />
                        </div>
                    </div>

                    <div className="articulos-toolbar-meta">
                        <div className="articulos-field articulos-field--compact">
                            <label className="articulos-label" htmlFor="pageSizeSelect">
                                Mostrar
                            </label>
                            <select id="pageSizeSelect" className="articulos-select" value={pageSize} onChange={handlePageSizeChange}>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="articulos-table-card">
                    <div className="articulos-table-scroll">
                        <table className="articulos-table hoverable-table">
                            <thead>
                                <tr>
                                    <th>Articulo</th>
                                    <th>Hoteles</th>
                                    <th>Descripcion</th>
                                    <th>Precios</th>
                                    <th>Impuestos</th>
                                    <th>Grupo</th>
                                    <th>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedArticulos.length > 0 ? (
                                    paginatedArticulos.map((articulo) => {
                                        const { intro, bullets } = formatDescriptionLines(articulo.desc_articulo);

                                        return (
                                        <tr key={articulo.id_articulo} onClick={() => handleOpenEditModal(articulo)} className="articulos-row">
                                            <td>
                                                <div className="articulos-name-cell">
                                                    <strong>{articulo.nombre_articulo}</strong>
                                                    <span>ID {articulo.id_articulo}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="articulos-chip-wrap">
                                                    {getSortedHotelIds(articulo, selectedHotelValues).length > 0
                                                        ? getSortedHotelIds(articulo, selectedHotelValues).map((hotelId) => {
                                                              const hotel = hotelLabelByValue[hotelId] || hotelId;
                                                              const isSelectedHotel = selectedHotelValues.includes(hotelId);

                                                              return (
                                                        <span
                                                            key={`${articulo.id_articulo}-${hotelId}`}
                                                            className={`articulos-chip ${isSelectedHotel ? "articulos-chip--selected" : ""}`}
                                                        >
                                                            {hotel}
                                                        </span>
                                                              );
                                                          })
                                                        : getHotelLabels(articulo).map((hotel) => (
                                                              <span key={`${articulo.id_articulo}-${hotel}`} className="articulos-chip">
                                                                  {hotel}
                                                              </span>
                                                          ))}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="articulos-description">
                                                    {intro ? <div className="articulos-description-intro">{intro}</div> : null}
                                                    {bullets.length > 0 ? (
                                                        <div className="articulos-description-list">
                                                            {bullets.map((bullet, index) => (
                                                                <div key={`${articulo.id_articulo}-bullet-${index}`} className="articulos-description-item">
                                                                    {`• ${bullet}`}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="articulos-price-stack">
                                                    {getHotelPrices(articulo, selectedHotelValues).length > 0 ? (
                                                        getHotelPrices(articulo, selectedHotelValues).map(({ hotelId, hotelLabel, hotelPrice }) => {
                                                            const isSelectedHotel = selectedHotelValues.includes(hotelId);

                                                            return (
                                                            <div
                                                                key={`${articulo.id_articulo}-price-${hotelId}`}
                                                                className={`articulos-price-row ${isSelectedHotel ? "articulos-price-row--selected" : ""}`}
                                                            >
                                                                <span className={`articulos-price-hotel ${isSelectedHotel ? "articulos-price-hotel--selected" : ""}`}>
                                                                    {hotelLabel}
                                                                </span>
                                                                <strong className={`articulos-price ${isSelectedHotel ? "articulos-price--selected" : ""}`}>
                                                                    ${hotelPrice}
                                                                </strong>
                                                            </div>
                                                            );
                                                        })
                                                    ) : (
                                                        <strong className="articulos-price">${articulo.precio_art}</strong>
                                                    )}
                                                    <span className="articulos-price-base">Base: ${articulo.precio_art}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="articulos-chip-wrap">
                                                    <span className="articulos-chip articulos-chip--soft">{getTaxLabel(articulo.trece_articulo, "13%")}</span>
                                                    <span className="articulos-chip articulos-chip--soft">{getTaxLabel(articulo.diez_articulo, "10%")}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="articulos-badge articulos-badge--group">{articulo.grupo_articulo}</span>
                                            </td>
                                            <td>
                                                <span
                                                    className={`articulos-badge ${Number(articulo.estado_articulo) === 1 ? "articulos-badge--active" : "articulos-badge--inactive"}`}
                                                >
                                                    {Number(articulo.estado_articulo) === 1 ? "Activo" : "Inactivo"}
                                                </span>
                                            </td>
                                        </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="7">
                                            <div className="articulos-empty">
                                                <strong>No hay articulos para este filtro.</strong>
                                                <span>Ajusta nombre u hoteles para volver a ver resultados.</span>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="articulos-pagination">
                    <button className="articulos-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                        Anterior
                    </button>
                    <div className="articulos-page-indicator">
                        Pagina <strong>{currentPage}</strong> de <strong>{totalPages}</strong>
                    </div>
                    <button className="articulos-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                        Siguiente
                    </button>
                </div>
            </section>

            <CrearArticulo open={openCreateModal} handleClose={handleCloseCreateModal} fetchArticulos={fetchArticulos} type="lista" />
            {selectedArticulo && (
                <EditarArticulo
                    open={openEditModal}
                    handleClose={handleCloseEditModal}
                    fetchArticulos={fetchArticulos}
                    type="lista"
                    Articulo={selectedArticulo}
                />
            )}
        </div>
    );
};
